import { faRepeatAlt, faTimes } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";

import { ICartItem } from "common/types/carts";
import CatalogImage from "common/components/CatalogImage";
import Icon from "common/components/Icon";

import TextChip from "client/common/components/content/TextChip";
import CrossedPrice from "client/common/components/content/CrossedPrice";
import QuantityInput from "client/common/components/inputs/QuantityInput";

import styles from "../../styles/shared/CartItemSummary.module.less";

const noop = () => undefined;

type CartItemSummarySize = "s" | "m";

type BaseCartItemSummaryProps = {
  cartItem: ICartItem;
  size?: CartItemSummarySize;
  hasFrequency?: boolean;
  hasPatient?: boolean;
  hasSelections?: boolean;
  hasQuantity?: boolean;
  hasPrice?: boolean;
};

type CartItemSummaryQuantityUpdateProps =
  | {
      canUpdateQuantity: true;
      hasQuantity?: true;
      onUpdateQuantity: (quantity: number) => void;
    }
  | { canUpdateQuantity?: false; onUpdateQuantity?: never };

type CartItemSummaryRemoveItemProps =
  | {
      canRemoveItem: true;
      onRemoveItem: () => void;
    }
  | { canRemoveItem?: false; onRemoveItem?: never };

type CartItemSummaryProps = BaseCartItemSummaryProps &
  CartItemSummaryQuantityUpdateProps &
  CartItemSummaryRemoveItemProps;

const CartItemSummary = ({
  cartItem,
  size = "m",
  hasPatient = true,
  hasSelections = true,
  hasFrequency = true,
  hasPrice = false,
  canUpdateQuantity = false,
  hasQuantity = canUpdateQuantity,
  onUpdateQuantity = noop,
  canRemoveItem = false,
  onRemoveItem = noop,
}: CartItemSummaryProps) => {
  return (
    <div key={cartItem.id} className={cx(styles.item, styles[`size-${size}`])}>
      <CatalogImage
        className={styles.image}
        image={cartItem.item.image}
        size="thumbnail"
        alt={cartItem.item.product.product_line.name}
      />
      <div className={styles.info}>
        <div className={styles.head}>
          <div className={styles.title}>{cartItem.item.product.product_line.name}</div>
          {canRemoveItem && (
            <button type="button" onClick={onRemoveItem} className={styles.removeButton}>
              <Icon icon={faTimes} />
            </button>
          )}
        </div>
        <div className={styles.description}>
          {cartItem.item.product_line_relative_name}
          {hasSelections && cartItem.flavors_display && ` · ${cartItem.flavors_display}`}
          {hasPatient && cartItem.patient && ` · ${cartItem.patient.name_display}`}
        </div>
        {hasFrequency && cartItem.subscription && (
          <div className={styles.metaData}>
            <TextChip size={size} variant="promotion">
              <Icon icon={faRepeatAlt} />
              {cartItem.subscription.frequency_display}
            </TextChip>
          </div>
        )}
        <div className={styles.bottom}>
          {hasQuantity &&
            (canUpdateQuantity ? (
              <div className={styles.quantityInput}>
                <QuantityInput.Debounce
                  size="s"
                  value={cartItem.quantity}
                  onChange={onUpdateQuantity}
                  min={1}
                />
              </div>
            ) : (
              <div className={styles.quantity}>Qty {cartItem.quantity}</div>
            ))}
          {hasPrice && (
            <div className={styles.price}>
              <CrossedPrice
                classNames={{ price: styles.mainPrice, crossed: styles.crossedPrice }}
                crossed={cartItem.subtotal}
                price={cartItem.discounted_subtotal}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartItemSummary;
